import React, { useState, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import moment from 'moment';
import forEach from 'lodash/forEach';
import keys from 'lodash/keys';
import find from 'lodash/find';
import isEqual from 'lodash/isEqual';
import _ from 'lodash';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { makeStyles } from '@material-ui/core';

import * as Constants from '../../../../../constants/charts';
import { getI18n } from "../../../../../utils/intl";

import Loading from "../../../../Loading";
import CompositeXYChart from '../../../CompositeXYChart';
import NoData from '../../../NoData';
import ChartLegend from '../../../ChartLegend';
import CustomLabelComponent from '../../../CustomLabelComponent';

import {
  getDateRangeData, getDisplayDate, lightShadeLineColors,
  getParamsFromQueryData, lineColors, getParamsForHistoricalData, clearMetricsHoverState,
  clearHistoricalDataMutations,
} from '../../../utils';
import { getXValueTick } from '../../../utils/filter';
import {
  getEntityPermuteMap,
  getBucketPermuteIdAndNames,
  getSourcesIdsFromResponse,
  getDefaultSharedMutations,
  getMetricKey,
  getSourceFromMetricKey,
  getMetricFromMetricKey,
} from '../../../utils/permute';

import { TooltipStyle } from '../../../ChartTooltip/index.theme';
import styles from './styles';

const HITS_METRICS_SUM = 'sum';
const HITS_METRIC_CUSTOM_DATE = 'customDate';
const HITS_METRIC_CUSTOM_WEEK = 'customWeek';

const createCustomLabelComponent = styles => {
  const StyledCustomLabelComponent = CustomLabelComponent(styles);
  return <StyledCustomLabelComponent />;
};

const getXAxisFormatter = (item, dateRangeData) => {
  const xAxisTickValuesMap = dateRangeData.xAxisTickValuesMap;
  if (xAxisTickValuesMap[item]) {
    return getDisplayDate(
      xAxisTickValuesMap[item],
      dateRangeData.dateRange,
      dateRangeData.timeAgg,
      dateRangeData.startDate,
      dateRangeData.endDate,
    );
  }
  return '';
};

const getI18nStrings = (key, intl) => {
  let i18nString = '';
  if (!_.isUndefined(key)) {
    const strObj = {
      dateTimeFormat: 'label.analytics.date.time.format.text',
      noDataText: 'label.analytics.dashboard.noData.text',
      apiHitsText: 'label.analytics.api.hits.text',
    };
    i18nString = intl.getI18nMessage(strObj[key]);
  }
  return i18nString;
};

const getHitsChartData = (xAxisData, hitsResponse, { apiMap, appMap, orgMap, intl }) => {
  // This is a special check, even when there are no transactions for a given
  // duration, backend sends an entry for each date under hitsTs. If there is
  // a fix from backend, then this check needs to be removed.
  if (hitsResponse && hitsResponse.data && hitsResponse.data.hits
    && hitsResponse.data.hits.hits === 0) {
    return [];
  }
  const permuteHitsTs =
    hitsResponse && hitsResponse.data && hitsResponse.data.hitsTs;
  const sortedPermuteHitsTs =
    (permuteHitsTs &&
      permuteHitsTs.sort((a, b) => (a.date > b.date ? 1 : -1))) ||
    [];
  const { timerange, timeAgg, startDate, endDate } = getParamsFromQueryData(
    hitsResponse && hitsResponse.query,
  );
  const hitsBuckets =
    (hitsResponse &&
      hitsResponse.data &&
      hitsResponse.data.hits &&
      hitsResponse.data.hits.buckets) ||
    [];
  const entitiesStr = (hitsResponse && hitsResponse.query.buckets) || '';
  const permuteMap = getEntityPermuteMap(hitsBuckets, entitiesStr, {
    apiMap,
    appMap,
    orgMap,
  });
  const permuteIds = keys(permuteMap);
  const chartData = [];
  permuteIds.forEach((permuteId, idx) => {
    const data = [];
    let permuteHitBucket = {};
    let totalApiHits = 0;
    sortedPermuteHitsTs.forEach(timeStampedData => {
      permuteHitBucket = find(timeStampedData.buckets, bucket => {
        const permuteObject = getBucketPermuteIdAndNames(
          bucket,
          entitiesStr,
          {
            apiMap,
            appMap,
            orgMap,
          },
        );
        return permuteObject.permuteId === permuteId;
      });
      if (!entitiesStr) {
        permuteHitBucket = timeStampedData;
      }
      const currentPermuteObject = permuteMap[permuteId];
      const dateTimeStr = getDisplayDate(
        moment.utc(timeStampedData.date).toISOString(),
        timerange,
        timeAgg,
        startDate,
        endDate,
        'tooltip',
      );
      const labelValue = permuteHitBucket && permuteHitBucket.hits ?
        permuteHitBucket.hits : 0;
      const dataObj = {
        x:
          xAxisData &&
          getXValueTick(
            xAxisData.xAxisValuesTickMap,
            timeStampedData.date,
          ),
        y:
          permuteHitBucket && permuteHitBucket.hits
            ? permuteHitBucket.hits
            : 0,
        label: `${currentPermuteObject.names.join(' > ')}\n\n${getI18nStrings('apiHitsText', intl)} : ${labelValue}\n${getI18nStrings('dateTimeFormat', intl)} :\n${dateTimeStr}`,
      };
      data.push(dataObj);
      totalApiHits += permuteHitBucket ? permuteHitBucket.hits : 0;
    });
    if (data.length) {
      chartData.push({
        customLabelComponent: createCustomLabelComponent(TooltipStyle),
        id: permuteId,
        names: permuteMap[permuteId].names,
        data,
        hits: totalApiHits,
        type: Constants.CHART_ELEMENT_LINE,
        colorIndex: idx,
      });
    }
  });
  return chartData;
};

const getHistoricalChartDataFromResponse = (hitsResponse, { apiMap, appMap, orgMap, intl }) => {
  const queryParams = getParamsFromQueryData(
    hitsResponse && hitsResponse.query,
  );
  const timerange = queryParams.timerange;
  const timeAgg = queryParams.timeAgg;
  const startDate = queryParams.startDate;
  const endDate = queryParams.endDate;
  // get dateRangeData passed, instead of calculating it
  const dateRangeData = getDateRangeData(
    timerange,
    timeAgg,
    startDate,
    endDate,
  );
  const historicalChartDataFromResponse = getHitsChartData(
    dateRangeData,
    hitsResponse,
    { apiMap, appMap, orgMap, intl },
  );
  return historicalChartDataFromResponse;
};

const getLineChartsData = (hitsData, metric, sharedMutations) => {
  const lineCharts = [];
  forEach(hitsData, item => {
    const { id, data, customLabelComponent } = item;
    let { colorIndex } = item;
    let pattern = '';
    let useLightShadedColor = false;
    const lineData = [];
    const sourceMutation = sharedMutations[id] || {};
    if (sourceMutation && sourceMutation.colorIndex !== undefined) {
      colorIndex = sourceMutation.colorIndex;
    }
    const metricsMutation = sourceMutation.metrics || {};
    if (metric === HITS_METRIC_CUSTOM_DATE) {
      pattern = 'dashed';
    }
    if (metric === HITS_METRIC_CUSTOM_WEEK) {
      useLightShadedColor = true;
    }
    const metricMutation = metricsMutation[metric] || {};
    const highLight = !!metricMutation.hover;
    if (!sourceMutation.show || !metricMutation.show) {
      return;
    }
    forEach(data, dataItem => {
      const { x, y, label } = dataItem;
      lineData.push({
        label,
        x,
        y,
      });
    });
    lineCharts.push({
      colorIndex,
      customLabelComponent,
      data: lineData,
      pattern,
      highLight,
      useLightShadedColor,
      id,
      metric,
      type: Constants.CHART_ELEMENT_LINE,
    });
  });
  return lineCharts;
};

const chartScale = { x: 'time' };

const HitsChart = (props) => {
  const {
    isLoading,
    defaultHitsResponse,
    fetchCustomDateMetricsData,
    is30DaysDataLoading,
    is52WeeksDataLoading,
    hitsResponseFor30DaysAgo,
    hitsResponseFor52WeeksAgo,
    apiMap, appMap, orgMap,
  } = props;

  const intl = getI18n(useIntl());
  const useStyles = makeStyles(styles);
  const classes = useStyles();

  const [state, updateState] = useState({
    sharedMutations: {},
    keyMetricDataTS: 0,
    key: '',
    metricEvent: '',
  });

  const setState = (value) => {
    if (typeof value === 'function') {
      updateState(currentValue => ({ ...currentValue, ...value(state) }));
    } else {
      updateState(currentValue => ({ ...currentValue, ...value }));
    }
  }

  const defaultHitsResponseRef = useRef(defaultHitsResponse);
  useEffect(() => {
    const entitiesMap = { apiMap, appMap, orgMap };
    const oldSourcesIds = (state.sharedMutations && keys(state.sharedMutations)) || [];
    const newSourcesIds = getSourcesIdsFromResponse(defaultHitsResponse, 'hits', entitiesMap);

    const hasNewSourceIds = newSourcesIds.length > 0;
    // reset sharedMutations if sources changes
    if (hasNewSourceIds &&
      !isEqual(oldSourcesIds.sort(), newSourcesIds.sort())) {
      const sharedMutations = getDefaultSharedMutations(newSourcesIds, HITS_METRICS_SUM);
      setState({
        sharedMutations,
        keyMetricDataTS: defaultHitsResponse?.timeStamp ?? 0,
      });
    } else if (hasNewSourceIds && defaultHitsResponse.timeStamp > state.keyMetricDataTS) {
      setState({
        sharedMutations: clearHistoricalDataMutations(state.sharedMutations),
        keyMetricDataTS: defaultHitsResponse?.timeStamp ?? 0,
      });
    }
    defaultHitsResponseRef.current = defaultHitsResponse;
  }, [defaultHitsResponse]);

  useEffect(() => {
    setState(
      prevState => {
        const { key, metricEvent } = prevState;
        const sharedMutations = prevState.sharedMutations || {};
        const sourceKey = metricEvent ? getSourceFromMetricKey(key) : key;
        const metricKey = getMetricFromMetricKey(key);
        const sourceMutation = (sharedMutations && sharedMutations[sourceKey]) || {};
        const metricsMutation = sourceMutation.metrics || {};
        const metricMutation = metricsMutation[metricKey];
        const defaultResponse = defaultHitsResponse;
        if (defaultResponse && defaultResponse.query) {
          if ((metricKey === HITS_METRIC_CUSTOM_DATE) && metricMutation.show) {
            const params = getParamsForHistoricalData(
              defaultResponse.query,
              Constants.HISTORICAL_METRIC_30DAYS,
              defaultResponse.data.hits,
            );
            fetchCustomDateMetricsData(params);
          }
          if ((metricKey === HITS_METRIC_CUSTOM_WEEK) && metricMutation.show) {
            const params = getParamsForHistoricalData(
              defaultResponse.query,
              Constants.HISTORICAL_METRIC_52WEEKS,
              defaultResponse.data.hits,
            );
            fetchCustomDateMetricsData(params);
          }
        }
      },
    );
  }, [state.key, state.metricEvent]);

  const chartHoverHandler = (id, metric, hoverState) => {
    legendHoverHandler(getMetricKey(id, metric), hoverState);
  };

  const legendHoverHandler = (key, hoverState) => {
    setState(prevState => {
      let sharedMutations = prevState.sharedMutations || {};
      const source = getSourceFromMetricKey(key);
      const sourceMutation = (sharedMutations && sharedMutations[source]) || {};
      const metric = getMetricFromMetricKey(key);
      const metricsMutation =
        (sourceMutation && sourceMutation.metrics) || {};
      if (hoverState) {
        // DE437921: we started using hover state to enable/disable tooltip
        // But, with VictoryVoronoiContainer data points from different metrics
        // can be in hover state, when they have same values. so, i am
        // clearing hover state of all metrics to keep only the latest one
        // as highlighted(i.e. only it's tooltips will be active).
        // so user would see only 1 tooltip
        sharedMutations = clearMetricsHoverState(sharedMutations);
      }
      const metricMutation =
        (metricsMutation && metricsMutation[metric]) || {};
      const updatedMutations = {
        ...sharedMutations,
        [source]: {
          ...sourceMutation,
          metrics: {
            ...metricsMutation,
            [metric]: {
              ...metricMutation,
              hover: hoverState,
            },
          },
        },
      };
      return {
        sharedMutations: updatedMutations,
      };
    });
  };

  const legendClickHandler = (key, metricEvent) => {
    const {
      timerange,
      timeAgg,
      startDate,
      endDate,
    } = getParamsFromQueryData(defaultHitsResponse && defaultHitsResponse.query);
    // get dateRangeData passed, instead of calculating it
    const dateRangeData = getDateRangeData(
      timerange,
      timeAgg,
      startDate,
      endDate,
    );
    const hitsChartData = getHitsChartData(
      dateRangeData, defaultHitsResponse, { apiMap, appMap, orgMap, intl },
    );
    // this shouldn't be required if it is set on props change
    const colorIndex = hitsChartData.find(
      data => {
        const id = key.includes('___') ? key.substring(0, key.indexOf('___')) : key;
        return data.id === id;
      },
    ).colorIndex;
    setState(prevState => {
      const sharedMutations = prevState.sharedMutations || {};
      const sourceKey = metricEvent ? getSourceFromMetricKey(key) : key;
      const metricKey = getMetricFromMetricKey(key);
      const sourceMutation = (sharedMutations && sharedMutations[sourceKey]) || {};
      const metricsMutation = sourceMutation.metrics || {};
      let updatedMutations = sharedMutations;
      if (metricEvent) {
        const metric = getMetricFromMetricKey(key);
        if (metric === HITS_METRICS_SUM) {
          updatedMutations = {
            ...sharedMutations,
            [sourceKey]: {
              ...sourceMutation,
              show: !sourceMutation.show,
              metrics: {
                [metricKey]: { show: !sourceMutation.show },
              },
            },
          };
        } else {
          const metricMutation = metricsMutation[metricKey] || {};
          updatedMutations = {
            ...sharedMutations,
            [sourceKey]: {
              show: true,
              metrics: {
                ...metricsMutation,
                sum: { show: true },
                [metricKey]: {
                  show: !metricMutation.show,
                },
              },
              colorIndex,
            },
          };
        }
      }
      return {
        sharedMutations: updatedMutations,
        key, metricEvent,
      };
    });
  };

  const getHitsLegend = (color, isLine, show, pattern) => {
    const legendLineItemClass =
      pattern === 'dashed'
        ? classes.lineLegendItem__dashed
        : classes.lineLegendItem;
    return (<div
      className={
        isLine
          ? legendLineItemClass
          : classes.blockLegendItem
      }
      style={{
        backgroundColor: pattern === 'dashed' ? 'inherit' : color,
        visibility: show ? 'visible' : 'hidden',
        border: pattern === 'dashed' ? `2px dashed ${color}` : 'none',
      }}
    />);
  };

  const getHitsLegendData = (
    defaultHitsData,
    hitsDataFor30DaysAgo = [],
    hitsDataFor52WeeksAgo = [],
  ) => {
    const hitsLegendData = [];
    const { sharedMutations } = state;

    forEach(defaultHitsData, (item, key) => {
      const sourceMutation = sharedMutations[item.id] || {};
      const sourceShow = !!sourceMutation.show;
      const metricsObj = (sourceMutation && sourceMutation.metrics) || {};
      const sumMetricKey = getMetricKey(item.id, HITS_METRICS_SUM);
      const daysMetricKey = getMetricKey(item.id, HITS_METRIC_CUSTOM_DATE);
      const weeksMetricKey = getMetricKey(item.id, HITS_METRIC_CUSTOM_WEEK);
      const selectedTimerange = defaultHitsResponse.query.timerange;
      let totalHitsFor30DaysAgo = '';
      let totalHitsFor52WeeksAgo = '';
      if (selectedTimerange === 'hour') {
        totalHitsFor30DaysAgo = getI18nStrings('noDataText', intl);
        totalHitsFor52WeeksAgo = getI18nStrings('noDataText', intl);
      } else {
        if (is30DaysDataLoading) {
          totalHitsFor30DaysAgo = 'loading';
        } else {
          totalHitsFor30DaysAgo = hitsDataFor30DaysAgo &&
          hitsDataFor30DaysAgo.find(data => data.id === item.id) &&
          hitsDataFor30DaysAgo.find(data => data.id === item.id).hits;
        }
        if (is52WeeksDataLoading) {
          totalHitsFor52WeeksAgo = 'loading';
        } else {
          totalHitsFor52WeeksAgo = hitsDataFor52WeeksAgo &&
          hitsDataFor52WeeksAgo.find(data => data.id === item.id) &&
          hitsDataFor52WeeksAgo.find(data => data.id === item.id).hits;
        }
        if (hitsResponseFor30DaysAgo && !totalHitsFor30DaysAgo) {
          totalHitsFor30DaysAgo = getI18nStrings('noDataText', intl);
        }
        if (hitsResponseFor52WeeksAgo && !totalHitsFor52WeeksAgo) {
          totalHitsFor52WeeksAgo = getI18nStrings('noDataText', intl);
        }
      }
      const daysMetricShow = !!(
        sourceShow &&
        metricsObj[HITS_METRIC_CUSTOM_DATE] &&
        sourceShow &&
        metricsObj[HITS_METRIC_CUSTOM_DATE].show
      );
      const weeksMetricShow = !!(
        sourceShow &&
        metricsObj[HITS_METRIC_CUSTOM_WEEK] &&
        sourceShow &&
        metricsObj[HITS_METRIC_CUSTOM_WEEK].show
      );

      const individualData = [];
      const color = lineColors[key % lineColors.length];
      const lightShadeColor = lightShadeLineColors[key % lightShadeLineColors.length];
      individualData.push(
        {
          id: sumMetricKey,
          label: 'label.analytics.trafficChart.legend.apiHits.text',
          value: item.hits,
          component: getHitsLegend(color, true, sourceShow),
          show: sourceShow,
          disable: false,
        },
        {
          id: daysMetricKey,
          label: 'label.analytics.trafficChart.legend.30daysLabel.text',
          value: (daysMetricShow && totalHitsFor30DaysAgo) || '',
          component: getHitsLegend(color, true, daysMetricShow, 'dashed'),
          show: daysMetricShow,
          disable: selectedTimerange === 'hour',
        },
        {
          id: weeksMetricKey,
          label: 'label.analytics.trafficChart.legend.52weeksLabel.text',
          value: (weeksMetricShow && totalHitsFor52WeeksAgo) || '',
          component: getHitsLegend(lightShadeColor, true, weeksMetricShow),
          show: weeksMetricShow,
          disable: selectedTimerange === 'hour',
        },
      );
      hitsLegendData.push({
        uuid: item.id,
        names: item.names,
        color,
        data: individualData,
        show: sourceShow,
      });
    });
    return hitsLegendData;
  };

  const renderChartComponent = () => {
    if (isLoading) {
      return (
        <div className={classes.loader}>
          <Loading />
        </div>
      );
    }

    return (
      <div className={classes.chartContainer}>
        <div className={classes.lineChartContainer}>
          <div className={classes.plcaeholderText}>
            {intl.getI18nMessage('label.analytics.trafficChart.placeholder.title.text')}
          </div>
          <CompositeXYChart
            chartData={lineChartsData}
            chartScale={chartScale}
            formatXAxisData={(item) => getXAxisFormatter(item, dateRangeData)}
            hoverHandler={chartHoverHandler}
            lineColors={lineColors}
            lightShadeLineColors={lightShadeLineColors}
            xAxisLabel={getI18nStrings('dateTimeFormat', intl)}
            xAxisValues={dateRangeData.xAxisTickValues}
            yAxisLabel={getI18nStrings('apiHitsText', intl)}
          />
        </div>
        <div className={clsx(classes.labelForPrint, classes.trafficChartLabel)}>
          {intl.getI18nMessage('label.analytics.lineChart.metric.text')}
        </div>
        <ChartLegend
          data={hitsLegendData}
          hoverHandler={legendHoverHandler}
          clickHandler={legendClickHandler}
        />
      </div>
    );
  };

  const {
    timerange,
    timeAgg,
    startDate,
    endDate,
  } = getParamsFromQueryData(defaultHitsResponse && defaultHitsResponse.query);
  // get dateRangeData passed, instead of calculating it
  const dateRangeData = getDateRangeData(
    timerange,
    timeAgg,
    startDate,
    endDate,
  );
  const hitsChartData = getHitsChartData(
    dateRangeData, defaultHitsResponse, { apiMap, appMap, orgMap, intl },
  );

  let hitsLegendData = getHitsLegendData(hitsChartData);
  const lineChartsData = getLineChartsData(
    hitsChartData, HITS_METRICS_SUM, state.sharedMutations,
  ) || [];
  const historicalChartDataFor30Days = [];
  const historicalChartDataFor52Weeks = [];
  if (hitsResponseFor30DaysAgo) {
    historicalChartDataFor30Days.push(...getHistoricalChartDataFromResponse(
      hitsResponseFor30DaysAgo, { apiMap, appMap, orgMap, intl },
    ));
    const lineChartDataFor30Days = getLineChartsData(
      historicalChartDataFor30Days,
      HITS_METRIC_CUSTOM_DATE,
      state.sharedMutations,
    );
    lineChartsData.push(...lineChartDataFor30Days);
  }
  if (hitsResponseFor52WeeksAgo) {
    historicalChartDataFor52Weeks.push(...getHistoricalChartDataFromResponse(
      hitsResponseFor52WeeksAgo, { apiMap, appMap, orgMap, intl },
    ));
    const lineChartDataFor52Weeks = getLineChartsData(
      historicalChartDataFor52Weeks,
      HITS_METRIC_CUSTOM_WEEK,
      state.sharedMutations,
    );
    lineChartsData.push(...lineChartDataFor52Weeks);
  }
  if (historicalChartDataFor52Weeks.length || historicalChartDataFor30Days.length) {
    hitsLegendData = getHitsLegendData(
      hitsChartData,
      historicalChartDataFor30Days,
      historicalChartDataFor52Weeks,
    );
  }

  return (
    <div
      className={classes.apiTrafficChartContainer}
      data-apim-test="chart-hits"
    >
      <div className={classes.trafficChartLabel}>
        <div className={classes.chartTitle}>
          {intl.getI18nMessage('label.analytics.apiTrafficChart.title.text')}
        </div>
        <div className={classes.metricsLabel}>
          {intl.getI18nMessage('label.analytics.lineChart.metric.text')}
        </div>
      </div>
      {!hitsChartData.length && !isLoading ? <NoData /> : renderChartComponent()}
    </div>
  );
}

HitsChart.propTypes = {
  defaultHitsResponse: PropTypes.array,
  apiMap: PropTypes.shape(),
  appMap: PropTypes.shape(),
  orgMap: PropTypes.shape(),
  isLoading: PropTypes.bool,
  fetchCustomDateMetricsData: PropTypes.func,
  hitsResponseFor30DaysAgo: PropTypes.object,
  hitsResponseFor52WeeksAgo: PropTypes.object,
  is30DaysDataLoading: PropTypes.bool,
  is52WeeksDataLoading: PropTypes.bool,
};

export default HitsChart;